import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Spinner } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Search, List, Info } from "lucide-react";

// Loads a role-gated screenshot via axios (token in header) and renders it as an object URL.
function ManualImage({ src, caption }) {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let active = true;
    let objectUrl;
    api.get(src, { responseType: "blob" })
      .then((r) => { if (active) { objectUrl = URL.createObjectURL(r.data); setUrl(objectUrl); } })
      .catch(() => { if (active) setErr(true); });
    return () => { active = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [src]);
  return (
    <figure className="my-4" data-testid="manual-figure">
      <div className="rounded-lg border border-border overflow-hidden bg-secondary/40">
        {url ? (
          <img src={url} alt={caption} className="w-full h-auto block" />
        ) : (
          <div className="flex items-center justify-center h-40 text-xs text-muted-foreground">
            {err ? "Screenshot unavailable" : "Loading screenshot…"}
          </div>
        )}
      </div>
      {caption && <figcaption className="text-xs text-muted-foreground mt-2 italic">{caption}</figcaption>}
    </figure>
  );
}

// Non-interactive, tiled identity watermark. Hidden from print and from assistive tech.
function Watermark({ label }) {
  const cells = Array.from({ length: 72 });
  return (
    <div aria-hidden="true" data-testid="manual-watermark"
      className="pointer-events-none select-none absolute inset-0 overflow-hidden z-10 print:hidden">
      <div className="flex flex-wrap gap-x-16 gap-y-24 -rotate-[30deg] scale-150 opacity-[0.06] -translate-x-16 -translate-y-8">
        {cells.map((_, i) => (
          <span key={i} className="whitespace-nowrap text-sm font-semibold uppercase tracking-wider text-navy dark:text-white">
            {label} — Confidential · For evaluation only
          </span>
        ))}
      </div>
    </div>
  );
}

function Block({ b }) {
  if (b.type === "h2") return <h3 className="font-display text-lg font-semibold text-primary mt-6 mb-2">{b.text}</h3>;
  if (b.type === "h3") return <h4 className="font-semibold text-sm mt-4 mb-1 text-slate-800 dark:text-slate-200">{b.text}</h4>;
  if (b.type === "p") return <p className="text-sm leading-relaxed text-slate-700 dark:text-slate-300 my-2">{b.text}</p>;
  if (b.type === "note")
    return (
      <div className="flex gap-2 rounded-lg border-l-4 border-gold bg-gold/5 p-3 my-3" data-testid="manual-note">
        <Info className="h-4 w-4 text-gold shrink-0 mt-0.5" />
        <p className="text-xs leading-relaxed text-slate-700 dark:text-slate-300">{b.text}</p>
      </div>
    );
  if (b.type === "bullets")
    return (
      <ul className="list-disc pl-5 space-y-1 my-2">
        {b.items.map((it, i) => <li key={i} className="text-sm leading-relaxed text-slate-700 dark:text-slate-300">{it}</li>)}
      </ul>
    );
  if (b.type === "table")
    return (
      <div className="my-3 overflow-x-auto rounded-lg border border-border">
        <Table>
          <TableHeader><TableRow>{b.heads.map((h, i) => <TableHead key={i} className="text-xs whitespace-nowrap">{h}</TableHead>)}</TableRow></TableHeader>
          <TableBody>
            {b.rows.map((row, ri) => (
              <TableRow key={ri}>{row.map((cell, ci) => <TableCell key={ci} className="text-xs align-top">{cell}</TableCell>)}</TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  if (b.type === "img") return <ManualImage src={b.src} caption={b.caption} />;
  return null;
}

function blockText(b) {
  if (b.type === "p" || b.type === "note" || b.type === "h2" || b.type === "h3") return b.text || "";
  if (b.type === "bullets") return (b.items || []).join(" ");
  if (b.type === "table") return [...b.heads, ...b.rows.flat()].join(" ");
  if (b.type === "img") return b.caption || "";
  return "";
}

export default function Manual() {
  const { user } = useAuth();
  const wmLabel = `${user?.name || "User"}${user?.email ? " · " + user.email : ""}`;
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null);
  const [navOpen, setNavOpen] = useState(false);
  const refs = useRef({});

  useEffect(() => {
    api.get("/manual").then((r) => { setData(r.data); setActive(r.data.sections[0]?.id || null); })
      .catch((e) => toast.error(apiErr(e)));
  }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    const term = q.trim().toLowerCase();
    if (!term) return data.sections;
    return data.sections
      .map((s) => {
        const titleHit = s.title.toLowerCase().includes(term);
        const blocks = titleHit ? s.blocks : s.blocks.filter((b) => blockText(b).toLowerCase().includes(term));
        return blocks.length ? { ...s, blocks } : null;
      })
      .filter(Boolean);
  }, [data, q]);

  const scrollTo = (id) => {
    setActive(id);
    setNavOpen(false);
    const el = refs.current[id];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (!data) return <Spinner label="Loading manual…" />;

  const TocList = () => (
    <nav className="space-y-0.5" data-testid="manual-toc">
      {data.sections.map((s) => (
        <button key={s.id} data-testid={`manual-toc-${s.key}`} onClick={() => scrollTo(s.id)}
          className={`block w-full text-left text-sm rounded-md px-3 py-2 transition-colors ${active === s.id ? "bg-primary text-primary-foreground font-medium" : "text-slate-600 dark:text-slate-300 hover:bg-secondary"}`}>
          {s.title}
        </button>
      ))}
    </nav>
  );

  return (
    <div>
      <PageHeader overline={data.version} title="User Manual"
        subtitle={`${data.brand} · Sections shown for your role (${data.role.replace(/_/g, " ")}).`}
        testid="manual-header"
        actions={
          <Sheet open={navOpen} onOpenChange={setNavOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" className="gap-1 xl:hidden" data-testid="manual-toc-toggle"><List className="h-4 w-4" /> Contents</Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80 overflow-y-auto">
              <div className="mt-6"><TocList /></div>
            </SheetContent>
          </Sheet>
        } />

      <div className="relative mb-5 max-w-md print:hidden">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search the manual…"
          className="pl-9" data-testid="manual-search" />
      </div>

      {/* Print output: manual body is hidden; only this confidential notice is printed. */}
      <div className="hidden print:block text-center py-16" data-testid="manual-print-notice">
        <p className="font-semibold text-lg">Confidential — For evaluation only.</p>
        <p className="text-sm mt-1">{wmLabel}</p>
        <p className="text-xs mt-3 max-w-md mx-auto">This manual is view-only within the application and is not available for printing or export. Downloadable copies are restricted to authorised REEDOS administrators.</p>
      </div>

      <div className="flex gap-8 items-start print:hidden">
        <aside className="hidden xl:block w-72 shrink-0 sticky top-24 max-h-[calc(100vh-7rem)] overflow-y-auto">
          <Card className="p-3"><TocList /></Card>
        </aside>

        <div className="flex-1 min-w-0 relative">
          <Watermark label={wmLabel} />
          <div
            className="space-y-8 select-none"
            style={{ WebkitUserSelect: "none", MozUserSelect: "none", msUserSelect: "none", userSelect: "none" }}
            data-testid="manual-content"
            onContextMenu={(e) => e.preventDefault()}
            onCopy={(e) => e.preventDefault()}
            onCut={(e) => e.preventDefault()}
            onDragStart={(e) => e.preventDefault()}
          >
            {filtered.length === 0 && (
              <Card className="p-8 text-center text-sm text-muted-foreground" data-testid="manual-no-results">
                No sections match “{q}”.
              </Card>
            )}
            {filtered.map((s) => (
              <section key={s.id} id={s.id} ref={(el) => (refs.current[s.id] = el)} className="scroll-mt-24" data-testid={`manual-section-${s.key}`}>
                <Card className="p-5 sm:p-7">
                  <h2 className="font-display text-2xl font-bold text-navy dark:text-white mb-1">{s.title}</h2>
                  <div className="h-0.5 w-16 bg-gold rounded-full mb-4" />
                  {s.blocks.map((b, i) => <Block key={i} b={b} />)}
                </Card>
              </section>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
